import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { fmtErr } from "../lib/api";
import { toast } from "sonner";
import { MoonStar } from "lucide-react";
import { PasswordInput } from "../components/PasswordInput";

export default function Auth() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [birthDay, setBirthDay] = useState("");
  const [birthMonth, setBirthMonth] = useState("");
  const [birthYear, setBirthYear] = useState("");
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [gender, setGender] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const MESI = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
  const birthISO = birthDay && birthMonth && birthYear
    ? `${birthYear}-${String(birthMonth).padStart(2, "0")}-${String(birthDay).padStart(2, "0")}` : "";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const data = mode === "login" ? await login(email, password) : await register(name, email, password, birthISO, ageConfirmed, gender);
      toast.success(mode === "login" ? `Bentornata, ${data.name}!` : `Benvenuta, ${data.name}!`);
      const redirect = params.get("redirect");
      navigate(redirect || (data.role === "admin" ? "/admin" : "/cliente"));
    } catch (err) {
      setError(fmtErr(err, "Accesso non riuscito. Controlla i dati inseriti."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-20" data-testid="auth-page">
      <div className="mystic-card p-8 sm:p-10">
        <div className="text-center mb-8">
          <MoonStar className="w-8 h-8 text-amber-400 mx-auto mb-3" />
          <h1 className="font-display text-3xl text-amber-100">{mode === "login" ? "Accedi" : "Crea il tuo account"}</h1>
        </div>

        <div className="grid grid-cols-2 gap-2 mb-8 p-1 rounded-full bg-[#0f0617] border border-[#4a2175]" data-testid="auth-mode-switch">
          <button
            onClick={() => { setMode("login"); setError(""); }}
            data-testid="auth-mode-login"
            className={`py-2 rounded-full text-sm transition-all ${mode === "login" ? "bg-amber-500/15 text-amber-200 border border-amber-500/40" : "text-purple-300/60"}`}
          >
            Accedi
          </button>
          <button
            onClick={() => { setMode("register"); setError(""); }}
            data-testid="auth-mode-register"
            className={`py-2 rounded-full text-sm transition-all ${mode === "register" ? "bg-amber-500/15 text-amber-200 border border-amber-500/40" : "text-purple-300/60"}`}
          >
            Registrati
          </button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Il tuo nome"
              data-testid="auth-name-input"
              className="input-dark"
            />
          )}
          {mode === "register" && (
            <div data-testid="auth-birthdate-block">
              <p className="font-cinzel text-[11px] tracking-[0.25em] uppercase text-amber-400/80 mb-2">La tua data di nascita</p>
              <div className="grid grid-cols-3 gap-2">
                <select required value={birthDay} onChange={(e) => setBirthDay(e.target.value)} data-testid="auth-birth-day" className="input-dark">
                  <option value="">Giorno</option>
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
                <select required value={birthMonth} onChange={(e) => setBirthMonth(e.target.value)} data-testid="auth-birth-month" className="input-dark">
                  <option value="">Mese</option>
                  {MESI.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                </select>
                <select required value={birthYear} onChange={(e) => setBirthYear(e.target.value)} data-testid="auth-birth-year" className="input-dark">
                  <option value="">Anno</option>
                  {Array.from({ length: 108 }, (_, i) => new Date().getFullYear() - 18 - i).map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <label className="flex items-start gap-2.5 mt-3 cursor-pointer" data-testid="auth-age-confirm-label">
                <input
                  type="checkbox"
                  checked={ageConfirmed}
                  onChange={(e) => setAgeConfirmed(e.target.checked)}
                  data-testid="auth-age-confirm"
                  className="mt-0.5 accent-amber-500"
                />
                <span className="text-[11px] text-purple-200/70 leading-relaxed">
                  Confermo che la data di nascita è corretta e dichiaro di avere almeno 18 anni.
                </span>
              </label>
            </div>
          )}
          {mode === "register" && (
            <div data-testid="auth-gender-block">
              <p className="font-cinzel text-[11px] tracking-[0.25em] uppercase text-amber-400/80 mb-2">Come ti identifichi?</p>
              <div className="grid grid-cols-1 gap-1.5">
                {[["donna", "Donna"], ["uomo", "Uomo"], ["non_specificato", "Preferisco non specificarlo"]].map(([v, l]) => (
                  <button key={v} type="button" onClick={() => setGender(v)} data-testid={`auth-gender-${v}`}
                    className={`px-4 py-2.5 rounded-md text-sm text-left border transition-colors ${gender === v ? "border-amber-500/60 bg-amber-500/10 text-amber-200" : "border-[#4a2175] bg-[#180a26] text-purple-200/80 hover:border-amber-500/30"}`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
          )}
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            data-testid="auth-email-input"
            className="input-dark"
          />
          <PasswordInput
            required
            minLength={6}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password (min. 6 caratteri)"
            data-testid="auth-password-input"
            className="input-dark"
          />
          {error && <p className="text-red-400 text-sm" data-testid="auth-error">{error}</p>}
          <button type="submit" disabled={busy || (mode === "register" && (!birthISO || !ageConfirmed || !gender))} data-testid="auth-submit-btn" className="gold-btn w-full py-3 text-sm disabled:opacity-60">
            {busy ? "Attendi…" : mode === "login" ? "Accedi" : "Registrati"}
          </button>
          {mode === "login" && (
            <div className="text-center">
              <Link to="/recupera-password" data-testid="auth-forgot-link" className="text-xs text-amber-300/80 hover:text-amber-200 underline underline-offset-2 transition-colors">
                Password dimenticata?
              </Link>
            </div>
          )}
          <p className="text-[11px] text-purple-300/50 text-center leading-relaxed" data-testid="auth-legal-note">
            Continuando dichiari di avere almeno 18 anni e accetti i{" "}
            <Link to="/termini" className="text-amber-300/80 hover:text-amber-200 underline underline-offset-2">Termini e Condizioni</Link>, la{" "}
            <Link to="/privacy" className="text-amber-300/80 hover:text-amber-200 underline underline-offset-2">Privacy Policy</Link> e il{" "}
            <Link to="/disclaimer" className="text-amber-300/80 hover:text-amber-200 underline underline-offset-2">Disclaimer</Link>.
          </p>
        </form>
      </div>
    </div>
  );
}
